import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  TablePagination,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useQuery } from "react-query";

import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { getClinicianProfile, getSessions } from "../../../api/services/api";
import EmptyScreen from "../../../components/EmptyScreen";
import { ViewButton } from "../../../components/IconButtons";
import Loader from "../../../components/Loader";
import Table from "../../../components/TableComponent";
import textCapitalization from "../../../components/textCapitalization";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

interface ModeOption {
  mode: string;
}

export const UpcomingSessions = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [modeValue, setModeValue] = useState<ModeOption[]>([]);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  const [status] = useState([
    "PENDING",
    "PAID",
    "STARTED",
  ]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const { data: profileData } = useQuery(
    ["clinician-profile"],
    getClinicianProfile
  );

  const { data, isLoading } = useQuery(
    [
      "sessions",
      {
        status: status,
        search: search,
        modeOfConsultation: modeValue
          .map((mode) => mode.mode.toUpperCase())
          .join(","),
        fromDate: startDate,
        toDate: endDate,
        clinicianId: profileData?.data?.id,
        limit: limit,
        offset: limit * page,
      },
    ],
    getSessions
  );

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: any) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const columns = [
    {
      title: "Session ID",
      dataIndex: "sessionId",
      key: "sessionId",
      render: (colData: any) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.primary.main }}>
          #{colData}
        </Typography>
      ),
    },
    {
      title: "Client Details",
      dataIndex: "patientProfiles.fullName",
      key: "patientProfiles.fullName",
      render: (colData: any, row: any) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row?.patientProfiles?.fullName || row?.patients?.users?.fullName || "N/A"}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.colors.alpha.black[70] }}>
            {row?.patientProfiles?.age} yrs • {row?.patientProfiles?.gender}
          </Typography>
          {row?.patients?.users?.mobileNumber && (
            <Typography variant="caption" sx={{ display: "block", color: theme.colors.alpha.black[50] }}>
              {row?.patients?.users?.mobileNumber}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      title: "Session Date & Time",
      dataIndex: "date",
      key: "date",
      render: (colData: any, row: any) => {
        const displayDate = (() => {
          if (colData) return colData;
          try {
            const desc = JSON.parse(row?.description || '{}');
            if (desc?.timeslotsByDate && desc.timeslotsByDate.length > 0) {
              return desc.timeslotsByDate[0].date;
            }
          } catch (e) { }
          return null;
        })();

        const timeslots = row?.sessionTimeslots || row?.session_timeslot || row?.sessionTimeslot || [];

        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {displayDate ? moment(displayDate).format("DD MMM YYYY") : "N/A"}
            </Typography>
            {timeslots.length > 0 ? (
              timeslots.map((item: any, idx: number) => {
                const slot = item?.timeslot || item?.time_slots || item?.timeSlots;
                const startAt = slot?.startAt || slot?.start_at;
                const endAt = slot?.endAt || slot?.end_at;
                return (
                  <Typography key={idx} variant="caption" sx={{ display: "block", color: theme.colors.alpha.black[70] }}>
                    {startAt && endAt ? `${moment(startAt, "HH:mm").format("h:mm A")} - ${moment(endAt, "HH:mm").format("h:mm A")}` : "Time N/A"}
                  </Typography>
                );
              })
            ) : (
              <Typography variant="caption" sx={{ color: theme.colors.alpha.black[50] }}>
                Time not scheduled
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      title: "Booked On",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (colData: any) => (
        <Box>
          <Typography variant="body2">
            {moment(colData).format("DD MMM YYYY")}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.colors.alpha.black[70] }}>
            {moment(colData).format("h:mm A")}
          </Typography>
        </Box>
      ),
    },
    {
      title: "Service & Mode",
      dataIndex: "consultationMode",
      key: "consultationMode",
      render: (colData: any, row: any) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {row?.service?.title || "N/A"}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.colors.alpha.black[70] }}>
            {textCapitalization(colData)}
          </Typography>
          {row?.symptom && (
            <Typography variant="caption" sx={{ display: "block", color: theme.colors.alpha.black[50], fontStyle: "italic" }}>
              {row?.symptom}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      title: "Payment",
      dataIndex: "sessionPayment.totalAmount",
      key: "sessionPayment.totalAmount",
      render: (colData: any, row: any) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.success.main }}>
            {row?.sessionPayment?.amount ? `${row.sessionPayment.amount} SAR` : "N/A"}
          </Typography>
          <Typography variant="caption" sx={{
            color: row?.sessionPayment?.status === "PAID" ? theme.colors.success.main : theme.colors.warning.main,
            fontWeight: 500
          }}>
            {row?.sessionPayment?.status || "N/A"}
          </Typography>
        </Box>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (colData: any) => {
        return (
          <Button
            sx={{
              py: 1,
              px: 2,
              background:
                colData === "PENDING"
                  ? `${theme.colors.warning.lighter}`
                  : colData === "PAID"
                    ? `${theme.colors.primary.lighter}`
                    : colData === "STARTED"
                      ? `${theme.colors.info.lighter}`
                      : colData === "COMPLETED"
                        ? `${theme.colors.secondary.lighter}`
                        : colData === "REPORT_SUBMITTED"
                          ? `${theme.colors.info.lighter}`
                          : colData === "CANCELLED"
                            ? `${theme.colors.error.lighter}`
                            : colData === "REJECTED"
                              ? `${theme.colors.error.lighter}`
                              : "",
              color:
                colData === "PENDING"
                  ? `${theme.colors.warning.main}`
                  : colData === "PAID"
                    ? `${theme.colors.primary.main}`
                    : colData === "STARTED"
                      ? `${theme.colors.info.main}`
                      : colData === "COMPLETED"
                        ? `${theme.colors.secondary.main}`
                        : colData === "REPORT_SUBMITTED"
                          ? `${theme.colors.info.main}`
                          : colData === "CANCELLED"
                            ? `${theme.colors.error.main}`
                            : colData === "REJECTED"
                              ? `${theme.colors.error.main}`
                              : "",
            }}
          >
            {colData}
          </Button>
        );
      },
    },

    {
      title: "Actions",
      dataIndex: "id",
      key: "id",
      render: (colData: any, row: any) => (
        <ViewButton
          onClick={() => {
            return navigate(`/sessions/upcoming-sessions/${row.id}`);
          }}
        />
      ),
    },
  ];

  const modesMenu = [
    {
      mode: "Video",
    },
    {
      mode: "Audio",
    },
    // {
    //   mode: "Home",
    // },
  ];

  return (
    <Box mt={2}>
      <Box display="flex" gap={1} mb={3} alignItems="center">
        <ArrowBackIcon
          sx={{ cursor: "pointer", "&:hover": { color: theme.colors.primary.main } }}
          onClick={() => navigate("/sessions")}
        />
        <Typography variant="h3">Upcoming Sessions</Typography>
        {data?.data?.count > 0 && (
          <Box sx={{
            ml: 2,
            px: 1.5,
            py: 0.5,
            backgroundColor: theme.colors.primary.lighter,
            borderRadius: 1
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.primary.main }}>
              {data?.data?.count} {data?.data?.count === 1 ? "Session" : "Sessions"}
            </Typography>
          </Box>
        )}
      </Box>
      <Box display="flex" gap={2} mb={2}>
        <TextField
          placeholder="Search by Session ID or Patient Name"
          sx={{ width: "450px" }}
          size="small"
          InputProps={{
            endAdornment: (
              <IconButton size="small">
                <SearchIcon />
              </IconButton>
            ),
          }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button
          variant="outlined"
          startIcon={<FilterListOutlinedIcon />}
          onClick={handleClick}
        >
          <Typography>Filter</Typography>
        </Button>
        <Menu
          open={open}
          onClose={handleClose}
          anchorEl={anchorEl}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                inputFormat="DD/MM/YYYY"
                value={startDate}
                onChange={(newValue: any) => {
                  setStartDate(moment(new Date(newValue)).format("YYYY-MM-DD"));
                }}
                renderInput={(params) => (
                  <TextField sx={{ width: "230px", mr: 1 }} {...params} />
                )}
              />
              <DatePicker
                label="End Date"
                inputFormat="DD/MM/YYYY"
                value={endDate}
                onChange={(newValue: any) => {
                  setEndDate(moment(new Date(newValue)).format("YYYY-MM-DD"));
                }}
                renderInput={(params) => (
                  <TextField sx={{ width: "230px" }} {...params} />
                )}
              />
            </LocalizationProvider>
          </MenuItem>

          <MenuItem>
            <Box sx={{ width: "100%" }}>
              <Autocomplete
                multiple
                options={modesMenu}
                value={modeValue}
                getOptionLabel={(option: any) => option.mode}
                isOptionEqualToValue={(option, value) =>
                  option.mode === value?.mode
                }
                onChange={(event: any, newValues: any) => {
                  setModeValue(newValues);
                }}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} style={{ marginRight: 8 }} />
                    <ListItemText primary={option.mode} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    label="Mode of Sessions"
                  />
                )}
              />
            </Box>
          </MenuItem>
        </Menu>
      </Box>
      <Box mt={2}>
        {data?.data.count === 0 ? (
          <EmptyScreen type="Sessions" />
        ) : (
          <>
            {isLoading ? (
              <Loader />
            ) : (
              <Box>
                <Table
                  headAlign={"center"}
                  tableAlign={"center"}
                  key={"sessions"}
                  dataSource={data?.data?.sessions}
                  rowKey={"id"}
                  columns={columns}
                  loading={isLoading}
                />
                <Box
                  sx={{
                    marginTop: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TablePagination
                    count={data?.data.count}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={limit}
                    onRowsPerPageChange={handleRowsPerPageChange}
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
